/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { FC, JSX, MouseEvent, RefObject } from 'react'

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuRenderFn,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import {
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  createCommand,
  PASTE_COMMAND,
  TextNode,
} from 'lexical'
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as ReactDOM from 'react-dom'

import { $createEntityReferenceNode } from '../../nodes/entityReferenceNode'
import { mergeRegister } from '@lexical/utils'
import { COMMAND_PRIORITY_EDITOR } from 'lexical'
import { useSearchEntitiesByEntityQuery } from '@crew/apis/crossSearch/crossSearchApis'
import { useLazyGetLookupRelatedItemQuery } from '@crew/apis/lookup/lookupApis'
import { SearchEntitiesByEntityRequest } from '@crew/apis/crossSearch/models/searchEntitiesByEntity/request'
import { skipToken } from '@reduxjs/toolkit/query'
import { EntityType } from '@crew/enums/domain'
import { CrewEntityIcon } from 'components/elements/crewEntityIcon/crewEntityIcon'

const PUNCTUATION =
  '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;'
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']'

// 参照候補の検出に利用する正規表現の構成要素
const DocumentEntityReferenceRegex = {
  NAME,
  PUNCTUATION,
}

const PUNC = DocumentEntityReferenceRegex.PUNCTUATION

const TRIGGERS = ['!'].join('')

// 参照キーワードとして許容する文字（空白・句読点・トリガー文字を除外）
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]'

// キーワード内で許容する結合パターン（例: 空白、句読点、ピリオド区切り）
// 前後が VALID_CHARS であることを前提にマッチする
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')'

const LENGTH_LIMIT = 75

// 「!keyword」形式の通常参照を検知する正規表現
const AtSignEntityReferenceRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$'
)

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50

// 参照候補ポップアップを開くためのカスタムコマンド
export const OPEN_REFERENCE_MODAL_COMMAND = createCommand<void>()

// エイリアス用（結合パターンを含まない）簡易検知の正規表現
const AtSignEntityReferenceRegexAliasRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    '){0,' +
    ALIAS_LENGTH_LIMIT +
    '})' +
    ')$'
)

const escapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// EntityType の値を正規表現に安全に埋め込むためにエスケープして結合
const entityReferenceEntityTypePattern = Object.values(EntityType)
  .map((value) => escapeRegex(value))
  .join('|')

// クリップボード貼り付け時の `!entityType:entityRecordId` 形式を検知
const entityReferencePasteRegex = new RegExp(
  `^!(${entityReferenceEntityTypePattern}):(.+)$`
)

// 入力中テキストから参照トリガーに一致する範囲を抽出
function checkForAtSignEntityReference(
  text: string,
  minMatchLength: number
): MenuTextMatch | null {
  let match = AtSignEntityReferenceRegex.exec(text)

  if (match === null) {
    match = AtSignEntityReferenceRegexAliasRegex.exec(text)
  }
  if (match !== null) {
    // 先頭空白はメニュー表示ロジック上は無視されるため、置換開始位置を補正する
    const maybeLeadingWhitespace = match[1]

    const matchingString = match[3]
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      }
    }
  }
  return null
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  return checkForAtSignEntityReference(text, 0)
}

// typeahead メニューで使用する候補データモデル
class EntityReferenceTypeaheadOption extends MenuOption {
  entityType: EntityType
  entityRecordId: string
  entityName: string
  icon: JSX.Element

  constructor(
    entityType: EntityType,
    entityRecordId: string,
    entityName: string,
    icon: JSX.Element
  ) {
    super(entityName)
    this.entityType = entityType
    this.entityRecordId = entityRecordId
    this.entityName = entityName
    this.icon = icon
  }
}

// OPEN_REFERENCE_MODAL_COMMAND 実行時に、カーソル直前に文字があるかを判定
// 直前に文字がある場合のみ空白を入れて `!` を挿入するために使う
const hasTextBeforeCursor = () => {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) {
    return false
  }

  const anchorNode = selection.anchor.getNode()
  const anchorOffset = selection.anchor.offset

  if (!$isTextNode(anchorNode)) {
    return false
  }

  const textBeforeCursor = anchorNode
    .getTextContent()
    .slice(0, anchorOffset)
    .trim()

  return textBeforeCursor.length > 0
}

// typeahead 候補1行分の表示コンポーネント
function EntityReferenceTypeaheadMenuItem({
  index,
  isSelected,
  onMouseDown,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number
  isSelected: boolean
  onMouseDown: (event: MouseEvent<HTMLLIElement>) => void
  onClick: () => void
  onMouseEnter: () => void
  option: EntityReferenceTypeaheadOption
}) {
  let className =
    'item crew-bg-default text-crew-gray-4-light dark:text-crew-gray-1-dark'
  if (isSelected) {
    className += ' selected dark:text-crew-gray-4-light'
  }
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <div className="flex items-center gap-x-2 min-w-0">
        {option.icon}
        <span className="text">{option.entityName}</span>
      </div>
    </li>
  )
}

type EntityReferencePluginProps = {
  entityType: EntityType
  entityRecordId: string
  disabled: boolean
  editorContainerRef?: RefObject<HTMLDivElement>
}

// `!` 入力による参照候補の検索・選択、および貼り付け変換を提供するプラグイン
export const EntityReferencePlugin: FC<EntityReferencePluginProps> = (
  props
) => {
  const [editor] = useLexicalComposerContext()
  const [lazyGetLookupRelatedItemQuery] = useLazyGetLookupRelatedItemQuery()

  const [queryString, setQueryString] = useState<string | null>(null)

  // クエリ文字列がある場合のみ API パラメータを組み立てる
  // なお、Pluginが読み込まれる際はqueryStringがnullのため、keywordを空文字でAPIリクエストを実行する
  // これは、結果をTypeaheadで表示するにあたり、事前にデータがセットされていないと
  // 画面下方向に表示領域が不足している場合にも画面外に切れて表示されてしまう問題があるため
  const searchEntitiesByEntityParams:
    | SearchEntitiesByEntityRequest
    | undefined = props.disabled
    ? undefined
    : {
        entityRecordId: props.entityRecordId,
        entityType: props.entityType,
        keyword: queryString === null ? '' : queryString, // クエリ文字列が null の場合は空文字を渡す（API側で空文字は全件検索として扱われる）
        limit: 10,
      }

  const { data: results } = useSearchEntitiesByEntityQuery(
    searchEntitiesByEntityParams ?? skipToken
  )

  // `!` をトリガーに typeahead を起動するためのマッチャ
  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('!', {
    minLength: 0,
  })

  // APIレスポンスを typeahead メニュー表示用のオプションに変換
  const options = useMemo(
    () =>
      results
        ? results.entities.map(
            (result) =>
              new EntityReferenceTypeaheadOption(
                result.entityType,
                result.entityRecordId,
                result.name,
                <CrewEntityIcon
                  entityType={result.entityType}
                  width={20}
                  height={20}
                />
              )
          )
        : [],
    [results]
  )

  const onSelectOption = useCallback(
    (
      selectedOption: EntityReferenceTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      // 候補選択時に ReferenceNode へ置換し、後続入力しやすいよう空白を1つ追加
      editor.update(() => {
        const entityReferenceNode = $createEntityReferenceNode(
          selectedOption.entityType,
          selectedOption.entityRecordId,
          selectedOption.entityName
        )
        if (nodeToReplace) {
          nodeToReplace.replace(entityReferenceNode)
          entityReferenceNode.insertAfter($createTextNode(' '))
        }
        closeMenu()
      })
    },
    [editor]
  )

  const checkForReferenceMatch = useCallback(
    (text: string) => {
      // 無効時は候補表示を行わない
      if (props.disabled) {
        return null
      }

      // `!` トリガーの成立確認
      const slashMatch = checkForSlashTriggerMatch(text, editor)
      if (slashMatch === null) {
        return null
      }

      // トリガー成立後、実際の置換対象文字列を抽出
      return getPossibleQueryMatch(text)
    },
    [checkForSlashTriggerMatch, editor, props.disabled]
  )

  // メニュークローズ時に、エディタ親のスクロールを復帰
  const handleEntityReferenceMenuItemClose = useCallback(() => {
    if (props.editorContainerRef?.current) {
      props.editorContainerRef.current.style.overflow = 'auto'
    }
  }, [props.editorContainerRef])

  const renderEntityReferenceMenuItem = useCallback<
    MenuRenderFn<EntityReferenceTypeaheadOption>
  >(
    (
      anchorElementRef,
      { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
    ) => {
      // 候補表示中はエディタ親のスクロールを止め、ポップアップ操作を優先
      if (anchorElementRef.current && options.length) {
        if (props.editorContainerRef?.current) {
          props.editorContainerRef.current.style.overflow = 'hidden'
        }

        return ReactDOM.createPortal(
          <div className="typeahead-popover entity-reference-menu">
            <ul>
              {options.map((option, i: number) => (
                <EntityReferenceTypeaheadMenuItem
                  index={i}
                  isSelected={selectedIndex === i}
                  onMouseDown={(event) => {
                    event.preventDefault()
                  }}
                  onClick={() => {
                    setHighlightedIndex(i)
                    selectOptionAndCleanUp(option)
                  }}
                  onMouseEnter={() => {
                    setHighlightedIndex(i)
                  }}
                  key={option.key}
                  option={option}
                />
              ))}
            </ul>
          </div>,
          anchorElementRef.current as HTMLElement
        )
      }

      return null
    },
    [options, props.editorContainerRef]
  )

  // クリップボードからペーストされたエンティティ参照を処理するためのハンドラ。
  const insertEntityReferenceFromClipboard = useCallback(
    (entityType: EntityType, entityRecordId: string) => {
      void (async () => {
        let entityName = `!${entityType}:${entityRecordId}`
        
        try {
          // 対象エンティティ名をAPIから取得し、ノードに使用する
          const response = await lazyGetLookupRelatedItemQuery({
            entityType,
            id: entityRecordId,
          }).unwrap()
          const relatedItemName = response.relatedItem?.name
          if (relatedItemName && relatedItemName.length > 0) {
            entityName = relatedItemName
          }
        } catch (error) {
          // API取得失敗時は貼り付け元のID表現を維持して後方互換を保つ
          console.warn(
            'Failed to resolve entity name from clipboard reference',
            error
          )
        }

        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            // カーソル位置にノードを挿入
            selection.insertNodes([
              $createEntityReferenceNode(entityType, entityRecordId, entityName),
            ])
          } else {
            // 選択範囲がない場合はルートの末尾に挿入
            $getRoot().append(
              $createEntityReferenceNode(entityType, entityRecordId, entityName)
            )
          }
        })
      })()
    },
    [editor, lazyGetLookupRelatedItemQuery]
  )

  useEffect(() => {
    // 参照関連コマンドをまとめて登録する
    const unregister = mergeRegister(
      editor.registerCommand<ClipboardEvent>(
        PASTE_COMMAND,
        (event) => {
          // 無効時は貼り付け変換を行わない
          if (props.disabled) {
            return false
          }

          // `!entityType:entityRecordId` 形式のテキスト貼り付けを ReferenceNode に変換
          const clipboardData = event.clipboardData
          if (clipboardData) {
            const text = clipboardData.getData('text/plain').trim()
            const match = entityReferencePasteRegex.exec(text)
            if (match !== null) {
              const entityType = match[1] as EntityType
              const entityRecordId = match[2]
              event.preventDefault()
              insertEntityReferenceFromClipboard(entityType, entityRecordId)
              return true
            }
          }
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        OPEN_REFERENCE_MODAL_COMMAND,
        () => {
          // 無効時は参照モーダルを開かない
          if (props.disabled) {
            return false
          }

          // 外部トリガーで参照入力を開始できるよう、`!` を現在カーソルへ挿入
          editor.focus()

          editor.update(() => {
            let selection = $getSelection()
            if (!$isRangeSelection(selection)) {
              $getRoot().selectEnd()
              selection = $getSelection()
            }

            if ($isRangeSelection(selection)) {
              // 直前に文字がある場合は区切りの空白を入れてから `!` を追加
              const prefix = hasTextBeforeCursor() ? ' ' : ''
              selection.insertText(`${prefix}!`)
            }
          })

          return true
        },
        COMMAND_PRIORITY_EDITOR
      )
    )

    return () => {
      // アンマウント時にコマンド登録を解除
      unregister()
    }
  }, [editor, insertEntityReferenceFromClipboard, props.disabled])

  return (
    <LexicalTypeaheadMenuPlugin<EntityReferenceTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForReferenceMatch}
      options={options}
      onClose={handleEntityReferenceMenuItemClose}
      menuRenderFn={renderEntityReferenceMenuItem}
    />
  )
}
